import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    fetchPlaybookCards,
    patchPlaybookCard,
    uploadTileImage,
} from "@/lib/api";
import { PageHeader } from "../_shared";

export default function PlaybookTab() {
    const [cards, setCards] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const refresh = useCallback(
        async () => setCards(await fetchPlaybookCards()),
        [],
    );
    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <div data-testid="playbook-tab">
            <PageHeader
                title="Playbook"
                accent="stat cards."
                description="The four numbered cards in the Expectations section. Edit number, label, and image."
            />
            <div
                data-testid="playbook-cards-grid"
                className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line rounded-2xl overflow-hidden"
            >
                {cards.map((c) => (
                    <PlaybookCardSlot
                        key={c.id}
                        card={c}
                        open={editingId === c.id}
                        onToggle={() =>
                            setEditingId(editingId === c.id ? null : c.id)
                        }
                        onSaved={async () => {
                            setEditingId(null);
                            await refresh();
                            toast("Playbook card updated.");
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

function PlaybookCardSlot({ card, open, onToggle, onSaved }) {
    const [number, setNumber] = useState(card.number || "");
    const [label, setLabel] = useState(card.label || "");
    const [imageUrl, setImageUrl] = useState(card.image_url || "");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        setNumber(card.number || "");
        setLabel(card.label || "");
        setImageUrl(card.image_url || "");
    }, [card, open]);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const res = await uploadTileImage(file);
            setImageUrl(res.url);
            toast("Image uploaded.");
        } catch (err) {
            toast(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
        }
    };

    const onSave = async () => {
        const l = label.trim();
        if (!l) {
            toast("Label is required.");
            return;
        }
        setBusy(true);
        try {
            await patchPlaybookCard(card.id, {
                number: number.trim(),
                label: l,
                image_url: imageUrl || null,
            });
            await onSaved?.();
        } catch (err) {
            toast(err?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    const origin = window.location.origin;
    const thumb = imageUrl
        ? imageUrl.startsWith("/")
            ? `${origin}${imageUrl}`
            : imageUrl
        : null;

    return (
        <div
            data-testid={`playbook-slot-${card.slot_index}`}
            className="bg-background"
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/[0.04] transition-colors"
            >
                <div className="w-16 h-16 shrink-0 border border-line bg-white/[0.03] overflow-hidden grid place-items-center rounded-xl">
                    {thumb ? (
                        <img
                            src={thumb}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <span className="text-ink-soft text-[0.65rem] font-display tracking-[0.2em]">
                            {number || "--"}
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[0.6rem] tracking-[0.25em] uppercase text-ink-soft">
                        Slot {String(card.slot_index + 1).padStart(2, "0")} ·{" "}
                        {number || "--"}
                    </div>
                    <div className="font-display font-bold truncate">
                        {label || "-"}
                    </div>
                </div>
                <div className="text-xs text-ink-soft">
                    {open ? "Close" : "Edit"}
                </div>
            </button>
            {open && (
                <div className="p-5 border-t border-line space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="sm:col-span-1">
                            <label
                                className="block text-[0.65rem] tracking-[0.25em] uppercase text-ink-soft mb-1"
                                htmlFor={`pb-num-${card.id}`}
                            >
                                Number
                            </label>
                            <input
                                id={`pb-num-${card.id}`}
                                data-testid={`playbook-number-input-${card.slot_index}`}
                                value={number}
                                onChange={(e) => setNumber(e.target.value)}
                                maxLength={8}
                                className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <label
                                className="block text-[0.65rem] tracking-[0.25em] uppercase text-ink-soft mb-1"
                                htmlFor={`pb-lbl-${card.id}`}
                            >
                                Label
                            </label>
                            <input
                                id={`pb-lbl-${card.id}`}
                                data-testid={`playbook-label-input-${card.slot_index}`}
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[0.65rem] tracking-[0.25em] uppercase text-ink-soft mb-1">
                            Image URL
                        </label>
                        <input
                            data-testid={`playbook-image-url-${card.slot_index}`}
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https:// or /api/files/…"
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                        />
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            onChange={onUpload}
                            className="hidden"
                            data-testid={`playbook-upload-${card.slot_index}`}
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="vc-btn vc-btn-ghost mt-2 text-xs"
                            disabled={busy}
                            data-testid={`playbook-upload-btn-${card.slot_index}`}
                        >
                            {busy ? "Uploading…" : "Upload image"}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={busy}
                        className="vc-btn"
                        data-testid={`playbook-save-${card.slot_index}`}
                    >
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}
